import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import Modal from './Modal';
import PhotoLightbox from './PhotoLightbox';
import ProductPickerPopover from './ProductPickerPopover';
import { nfConfirm } from './confirm';

/**
 * Приёмка накладной из биржи по строкам (04.11.2026, бэкенд: services/procurement_receiving.py,
 * procurement_claims.py). Общие куски для вкладки флориста «Приёмка» (pages/ReceivingPage.jsx) и
 * окна «Принять поставку» в «Складе» (pages/WarehousePage.jsx):
 *   • ReceivingLineEditor — экран одной строки: пришло, брак, причина, фото, оценки, «несколько товаров»;
 *   • ReceivingLineSummary — строка списка («○ не принята — можно взять» / «✓ 60 пришло, брак 12 …»);
 *   • ClaimsSection — рекламации накладной; HiddenDefectModal — «Брак после приёмки».
 * Окно админа «Принять поставку» и форма накладной из биржи — components/ProcurementInvoice.jsx.
 */

export const DEFECT_REASONS = [
  [1, 'Ломаный'], [2, 'Гнилой'], [3, 'Отваливаются бутоны'], [4, 'Осыпается листва'],
  [5, 'Старый'], [6, 'Не отпивается'], [7, 'Не хватает стеблей'], [8, 'Другое'],
];
const RATINGS = [
  ['rating_fresh', 'Свежесть и вид'],
  ['rating_match', 'Соответствие заказу (сорт, цвет, длина, кол-во)'],
  ['rating_vase', 'Стойкость в вазе'],
];
const ORIGIN = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
export const photoSrc = (url) => (!url ? null : url.startsWith('http') ? url : `${ORIGIN}${url}`);
const ddmm = (iso) => { if (!iso) return '—'; const [, m, d] = iso.slice(0, 10).split('-'); return `${d}.${m}`; };
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '');
const int = (v) => { const n = parseInt(String(v).replace(/\D/g, ''), 10); return Number.isFinite(n) ? n : 0; };
// Несколько товаров в строке — товар строки не участвует: «Mix Pastel · несколько товаров»
export const lineTitle = (l) => (l.splits?.length ? `${l.lot_short || l.lot_name} · несколько товаров`
  : (l.product_name || l.lot_name || 'Строка без товара'));
// «после приёмки −5 (Гнилой)»
export const hiddenText = (l) => (l.hidden_defects || []).map(h => `−${h.stems}${h.reason_label ? ` (${h.reason_label})` : ''}`).join(', ');

export function useNarrow(width = 700) {
  const get = () => typeof window !== 'undefined' && window.innerWidth < width;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const on = () => setNarrow(get());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  return narrow;
}

const card = { background: '#fff', border: '1px solid #EBEDEC', '--ch': '8px', '--stroke': '#EBEDEC', padding: 14 };
const numInput = {
  width: 84, padding: '8px 10px', textAlign: 'center', fontSize: 16, border: '1px solid #C1C8C4',
  '--ch': '5px', '--stroke': '#C1C8C4',
};
const sectionLabel = { fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#909894', textTransform: 'uppercase', marginBottom: 6 };

// ── Строка в списке ─────────────────────────────────────────────────────

// Строка списка на телефоне: «✓ 35 · Лена» / «✎ Аня» (открыта у другого) / пусто
export function lineBrief(l, me) {
  if (l.cancelled) return 'отменено на бирже';
  if (l.opened_by_name && l.opened_by_name !== me && !l.accepted) return `✎ ${l.opened_by_name}`;
  if (!l.accepted) return '';
  let t = `✓ ${l.actual_quantity}`;
  if (l.defect_quantity) t += ` · брак ${l.defect_quantity}`;
  const who = l.edited_by_name || l.accepted_by_name;
  if (who) t += ` · ${who}`;
  if (l.opened_by_name && l.opened_by_name !== me) t += ` · ✎ ${l.opened_by_name}`;
  return t;
}

export function lineStateText(l) {
  if (l.cancelled) return 'отменено на бирже';
  if (!l.accepted) return '○ не принята — можно взять';
  let t = `✓ ${l.actual_quantity} пришло`;
  if (l.defect_quantity) t += `, брак ${l.defect_quantity}${l.defect_reason_label ? ` (${l.defect_reason_label})` : ''}`;
  const who = l.edited_by_name || l.accepted_by_name;
  const at = l.edited_at || l.accepted_at;
  if (who) t += ` · ${who}${at ? `, ${hhmm(at)}` : ''}`;
  return t;
}

// «оценки 2 · 4 · ваза с 08.11» / «… · ваза 3»
// Оценок нет — пусто (или только «ваза с 08.11»), без «· · ·»
export const marks = (l, vaseFrom) => {
  const vaseSoon = vaseFrom ? `ваза с ${ddmm(vaseFrom)}` : '';
  if (!l.rating_fresh && !l.rating_match && !l.rating_vase) return vaseSoon;
  const one = (v) => (v ? String(v) : '·');
  const vase = l.rating_vase ? `ваза ${l.rating_vase}` : (vaseSoon || 'ваза ·');
  return `оценки ${one(l.rating_fresh)} · ${one(l.rating_match)} · ${vase}`;
};

// Сегодня по Москве — день поставки «сегодня / вчера / дд.мм»
const mskToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const dayShift = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export function relDay(iso) {
  if (!iso) return '—';
  const today = mskToday();
  if (iso === today) return 'сегодня';
  if (iso === dayShift(today, -1)) return 'вчера';
  if (iso === dayShift(today, 1)) return 'завтра';
  return ddmm(iso);
}
const plural = (n, one, few, many) => {
  const m10 = n % 10; const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

// «пришла сегодня · 20 позиций · принято 1 из 20»
export function deliverySubtitle(inv) {
  const day = inv.expected_date;
  const when = inv.status === 'accepted' ? 'принята на склад'
    : day && day <= mskToday() ? `пришла ${relDay(day)}` : `ожидается ${relDay(day)}`;
  const n = inv.lines_total || 0;
  return `${when} · ${n} ${plural(n, 'позиция', 'позиции', 'позиций')} · принято ${inv.lines_accepted || 0} из ${n}`;
}

export function ProgressBar({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total}
      style={{ height: 4, background: '#EBEDEC', marginTop: 8, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: '#1F763B', transition: 'width .3s' }} />
    </div>
  );
}

function NoPhoto({ size = 52 }) {
  return (
    <span aria-label="Нет фото" style={{ width: size, height: size, flexShrink: 0, display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: '#F4F4EF', border: '1px dashed #C1C8C4', '--ch': '4px', '--stroke': '#C1C8C4', color: '#A9B1AC' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M21 17l-5-5-8 7" />
      </svg>
      <span style={{ fontSize: 9 }}>нет фото</span>
    </span>
  );
}

export function ReceivingLineSummary({ line: l, onOpen, extra, vaseFrom, me }) {
  const brief = lineBrief(l, me);
  return (
    <div id={`rline-${l.id}`} role="button" tabIndex={0} onClick={() => !l.cancelled && onOpen(l)}
      onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !l.cancelled) { e.preventDefault(); onOpen(l); } }}
      style={{ ...card, display: 'flex', gap: 12, alignItems: 'center', cursor: l.cancelled ? 'default' : 'pointer',
        opacity: l.cancelled ? 0.55 : 1, background: l.accepted ? '#F3F7F4' : '#fff' }}>
      {l.lot_photo_url
        ? <img src={photoSrc(l.lot_photo_url)} alt="" style={{ width: 52, height: 52, objectFit: 'cover', '--ch': '4px', flexShrink: 0 }} />
        : <NoPhoto />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#1C3D2E', fontSize: 15 }}>{lineTitle(l)}</div>
        <div style={{ fontSize: 12, color: '#5E6561', marginTop: 2 }}>
          {l.extra ? 'лишнее, не заказывали' : <>{l.lot_name}{l.length_cm ? `, ${l.length_cm} см` : ''} · заказано {l.stems}</>}
          {l.splits?.length > 0 && ` · товаров: ${l.splits.length}`}
        </div>
        {(brief || l.needs_photo) && (
          <div style={{ fontSize: 13, marginTop: 4, color: l.accepted ? '#1F763B' : '#7E5D00', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {brief}{l.needs_photo && <span style={{ color: '#B0603C' }}>{brief ? ' · ' : ''}нет фото брака</span>}
          </div>
        )}
        {(l.photos.length > 0 || (l.accepted && marks(l, vaseFrom))) && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            {l.photos.slice(0, 4).map(ph => (
              <img key={ph.id} src={photoSrc(ph.url)} alt="" style={{ width: 28, height: 28, objectFit: 'cover', '--ch': '3px' }} />
            ))}
            {l.photos.length > 4 && <span style={{ fontSize: 12, color: '#909894' }}>+{l.photos.length - 4}</span>}
            {l.accepted && marks(l, vaseFrom) && <span style={{ fontSize: 12, color: '#5E6561', marginLeft: 4 }} title="Оценки: свежесть · соответствие · ваза">{marks(l, vaseFrom)}</span>}
          </div>
        )}
        {extra}
      </div>
      {!l.cancelled && <span aria-hidden="true" style={{ color: '#909894', fontSize: 20 }}>›</span>}
    </div>
  );
}

// ── Экран строки ────────────────────────────────────────────────────────

function Stars({ value, onChange, disabled }) {
  return (
    <div className="seg" role="radiogroup" style={{ opacity: disabled ? 0.5 : 1 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" role="radio" aria-checked={value === n} disabled={disabled}
          className={`seg-btn${value === n ? ' on' : ''}`} style={{ minWidth: 44, minHeight: 44, fontSize: 15 }}
          onClick={() => onChange(value === n ? null : n)}>{n}</button>
      ))}
    </div>
  );
}

// Число с «−» и «+» по бокам (кнопки 44 px — под палец)
const stepBtn = {
  width: 44, height: 44, border: '1px solid #C1C8C4', '--ch': '5px', '--stroke': '#C1C8C4', background: '#fff',
  fontSize: 20, color: '#1C3D2E', cursor: 'pointer', padding: 0, flexShrink: 0,
};
function Stepper({ value, onChange, disabled, label, readOnly = false, width = 72, autoFocus = false }) {
  const n = int(value);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {!readOnly && <button type="button" style={stepBtn} disabled={disabled || n <= 0} aria-label={`${label}: меньше`} onClick={() => onChange(String(Math.max(0, n - 1)))}>−</button>}
      <input type="text" inputMode="numeric" aria-label={label} value={value} disabled={disabled} readOnly={readOnly} autoFocus={autoFocus}
        onFocus={e => e.target.select()} onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
        style={{ ...numInput, width, height: 44, boxSizing: 'border-box', background: readOnly ? '#F4F4EF' : '#fff' }} />
      {!readOnly && <button type="button" style={stepBtn} disabled={disabled} aria-label={`${label}: больше`} onClick={() => onChange(String(n + 1))}>+</button>}
    </div>
  );
}

const isMixName = (name) => /микс|(^|[^a-z])mix([^a-z]|$)/i.test(name || '');

// Строка «несколько товаров»: товар (лупа по всему каталогу) + «пришло» / «брак» одной строкой
function ProductRow({ row, lineId, editable, onChange, onRemove }) {
  const btnRef = useRef(null);
  const [picking, setPicking] = useState(false);
  const load = useCallback(() => api.getReceivingProductOptions(lineId), [lineId]);
  const close = useCallback(() => setPicking(false), []);
  return (
    <div style={{ padding: '12px 0', borderTop: '1px solid #DCDDD3', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button ref={btnRef} type="button" disabled={!editable} onClick={() => setPicking(p => !p)}
          style={{ flex: 1, minWidth: 0, minHeight: 44, textAlign: 'left', padding: '8px 10px', border: `1px solid ${row.product_id ? '#C1C8C4' : '#B0603C'}`,
            '--ch': '5px', '--stroke': row.product_id ? '#C1C8C4' : '#B0603C', background: '#fff', cursor: editable ? 'pointer' : 'default',
            color: row.product_id ? '#1C3D2E' : '#B0603C', fontSize: 14, fontFamily: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.product_name || 'выбрать товар 🔍'}
        </button>
        {editable && (
          <button type="button" className="btn-icon is-danger" aria-label="Убрать товар" title="Убрать товар" onClick={onRemove}
            style={{ width: 44, height: 44 }}>×</button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: '0 12px', fontSize: 11, color: '#5E6561' }}>
        <span>пришло</span>
        <span>брак</span>
        <Stepper value={row.actual_quantity} disabled={!editable} label={`Пришло ${row.product_name || ''}, шт`} width={48} autoFocus={!!row.focus}
          onChange={(v) => onChange({ ...row, actual_quantity: v, focus: false })} />
        <Stepper value={row.defect_quantity} disabled={!editable} label={`Брак ${row.product_name || ''}, шт`} width={48}
          onChange={(v) => onChange({ ...row, defect_quantity: v })} />
      </div>
      {picking && (
        <ProductPickerPopover anchorRef={btnRef} loadOptions={load} onClose={close} title="Товар"
          onSelect={(p) => { setPicking(false); onChange({ ...row, product_id: p.id, product_name: p.name }); }} />
      )}
    </div>
  );
}

const toRows = (splits) => (splits || []).map(sp => ({
  id: sp.id, color: sp.color || '', product_id: sp.product_id, product_name: sp.product_name,
  actual_quantity: String(sp.actual_quantity ?? sp.quantity ?? 0), defect_quantity: String(sp.defect_quantity ?? 0),
}));

const errText = { fontSize: 13, color: '#CB4D45', marginTop: 6, fontWeight: 500 };

// «Лена изменила эту строку в 22:53: пришло 35, брак 2. [Взять её данные] [Оставить мои]»
const fem = (name) => /[ая]$/i.test((name || '').trim().split(/\s+/)[0] || '');
export function ConflictNote({ fresh, onTake, onKeep }) {
  const who = fresh.edited_by_name || fresh.accepted_by_name || 'Другой сотрудник';
  const at = fresh.edited_at || fresh.accepted_at;
  return (
    <div role="alert" style={{ padding: '8px 10px', background: '#F9EBCF', color: '#7E5D00', fontSize: 13, '--ch': '6px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      <span>{who} {fem(who) ? 'изменила' : 'изменил'} эту строку{at ? ` в ${hhmm(at)}` : ''}: пришло {fresh.actual_quantity ?? '—'}, брак {fresh.defect_quantity || 0}.</span>
      <button type="button" className="btn-link" onClick={onTake}>Взять её данные</button>
      <button type="button" className="btn-link" onClick={onKeep}>Оставить мои</button>
    </div>
  );
}

// initialDraft — введённое в строке таблицы, но не сохранённое (брак без причины/фото раскрыл строку):
// поля начинаются с него, а не с сохранённого (08.11)
export function ReceivingLineEditor({ line: initial, invoice, onSaved, onBack, showToast, fixedBar = false, embedded = false, initialDraft = null }) {
  const [line, setLine] = useState(initial);
  const [conflict, setConflict] = useState(null);  // 409: свежая строка от другого — введённое не трогаем
  const [actual, setActual] = useState(String(initialDraft?.actual ?? initial.actual_quantity ?? initial.stems));
  const [defect, setDefect] = useState(String(initialDraft?.defect ?? initial.defect_quantity ?? 0));
  const [reason, setReason] = useState(initial.defect_reason || null);
  const [note, setNote] = useState(initial.defect_note || '');
  const [rates, setRates] = useState({ rating_fresh: initial.rating_fresh, rating_match: initial.rating_match, rating_vase: initial.rating_vase });
  const [rows, setRows] = useState(initial.splits?.length ? toRows(initial.splits) : null);   // null — без разбивки
  const [unsplit, setUnsplit] = useState(false);   // «Убрать разбивку» — отправить splits: []
  const [adding, setAdding] = useState(false);     // «+ товар»: открыт выбор товара
  const addRef = useRef(null);
  const loadAll = useCallback(() => api.getReceivingProductOptions(initial.id), [initial.id]);
  const closeAdd = useCallback(() => setAdding(false), []);
  const [errors, setErrors] = useState({});        // { split, reason, photo } — под своим блоком
  const [busy, setBusy] = useState(false);
  const [viewAt, setViewAt] = useState(null);
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  const refs = { split: useRef(null), reason: useRef(null), photo: useRef(null) };
  // Раскрыли строку в таблице — фокус внутрь: на причину брака, если она нужна, иначе на сам блок
  useEffect(() => {
    if (!embedded) return;
    const first = refs.reason.current?.querySelector('button:not([disabled])');
    (first || rootRef.current)?.focus({ preventScroll: false });
  }, [embedded]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = (l) => {
    setLine(l);
    setActual(String(l.actual_quantity ?? l.stems));
    setDefect(String(l.defect_quantity ?? 0));
    setReason(l.defect_reason || null);
    setNote(l.defect_note || '');
    setRates({ rating_fresh: l.rating_fresh, rating_match: l.rating_match, rating_vase: l.rating_vase });
    setRows(l.splits?.length ? toRows(l.splits) : null);
    setUnsplit(false);
    setErrors({});
  };

  const finalAccepted = invoice.status === 'accepted';
  const editable = !finalAccepted && !line.cancelled;
  const vaseOpen = !!invoice.vase_open;
  const splitSum = rows ? rows.reduce((s, r) => s + int(r.actual_quantity), 0) : 0;
  // Несколько товаров — «Пришло» = сумма по товарам, «Брак» = сумма брака по товарам
  const actualTotal = rows ? splitSum : int(actual);
  const defectTotal = rows ? rows.reduce((s, r) => s + int(r.defect_quantity), 0) : int(defect);
  const needsDefectInfo = defectTotal > 0;

  const handleConflict = (error) => {
    const fresh = error.response?.data?.line;
    if (!fresh) return false;
    setConflict(fresh);   // введённое не перезаписываем — человек решает сам
    return true;
  };
  const takeTheirs = () => { reset(conflict); setConflict(null); };
  const keepMine = () => { setLine(l => ({ ...l, version: conflict.version, photos: conflict.photos })); setConflict(null); };

  // Открыл строку — у остальных «✎ <имя>» (раз в минуту, пока экран открыт)
  useEffect(() => {
    if (invoice.status === 'accepted' || initial.cancelled) return undefined;
    const ping = () => api.openReceivingLine(initial.id).catch(() => {});
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, [initial.id, initial.cancelled, invoice.status]);

  const fail = (key, text) => {
    setErrors({ [key]: text });
    setTimeout(() => refs[key].current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  };

  const save = async () => {
    if (rows && !rows.length) { fail('split', 'Добавьте хотя бы один товар или нажмите «Один товар»'); return; }
    if (rows && rows.some(r => !r.product_id)) { fail('split', 'У каждой строки нужен товар — нажмите «выбрать товар»'); return; }
    if (rows && rows.some(r => int(r.defect_quantity) > int(r.actual_quantity))) { fail('split', 'Брака товара больше, чем его пришло'); return; }
    if (needsDefectInfo && !reason) { fail('reason', 'Выберите причину брака'); return; }
    if (needsDefectInfo && !line.photos.length) { fail('photo', 'При браке нужно хотя бы одно фото — нажмите «+ Фото»'); return; }
    setErrors({});
    setBusy(true);
    try {
      const body = {
        version: line.version, actual_quantity: actualTotal, defect_quantity: defectTotal,
        defect_reason: needsDefectInfo ? reason : null, defect_note: needsDefectInfo ? (note.trim() || null) : null,
        rating_fresh: rates.rating_fresh || null, rating_match: rates.rating_match || null,
      };
      if (vaseOpen) body.rating_vase = rates.rating_vase || null;
      if (rows) body.splits = rows.map(r => ({ id: r.id || null, color: r.color || null, product_id: r.product_id,
        actual_quantity: int(r.actual_quantity), defect_quantity: int(r.defect_quantity) }));
      else if (unsplit) body.splits = [];
      const res = await api.saveReceivingLine(line.id, body);
      onSaved(res.line);
    } catch (error) {
      if (!handleConflict(error)) showToast(error.message || 'Не сохранилось', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveVase = async () => {
    setBusy(true);
    try {
      const res = await api.saveReceivingVase(line.id, line.version, rates.rating_vase || null);
      onSaved(res.line);
    } catch (error) {
      if (!handleConflict(error)) showToast(error.message || 'Не сохранилось', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    try {
      let res = null;
      for (const f of files) {
        if (!['image/jpeg', 'image/png'].includes(f.type)) { showToast('Нужен JPEG или PNG', 'error'); continue; }
        res = await api.addReceivingPhoto(line.id, f);   // eslint-disable-line no-await-in-loop
      }
      // Фото меняет version строки — иначе следующее «Строка принята» получило бы 409
      if (res) {
        setLine(l => ({ ...l, photos: res.line.photos, needs_photo: res.line.needs_photo, version: res.line.version }));
        setErrors(er => ({ ...er, photo: null }));
      }
    } catch (error) {
      showToast(error.message || 'Фото не загрузилось', 'error');
    } finally {
      setBusy(false);
    }
  };

  const deletePhoto = async (ph) => {
    if (!(await nfConfirm('Удалить фото?', { confirmText: 'Удалить' }))) return;
    setBusy(true);
    try {
      const res = await api.deleteReceivingPhoto(line.id, ph.id);
      setLine(l => ({ ...l, photos: res.line.photos, needs_photo: res.line.needs_photo, version: res.line.version }));
      setViewAt(null);
    } catch (error) {
      showToast(error.message || 'Фото не удалилось', 'error');
    } finally {
      setBusy(false);
    }
  };

  // «Несколько товаров»: первая строка — товар строки с полным количеством (кроме карточки «… микс»);
  // товара нет — сразу выбор. «+ товар» тоже сразу открывает выбор, количество — потом
  const startSplit = () => {
    setUnsplit(false);
    setErrors({});
    if (line.product_id && !isMixName(line.product_name)) {
      setRows([{ id: null, product_id: line.product_id, product_name: line.product_name, actual_quantity: actual, defect_quantity: defect }]);
    } else {
      setRows([]);
      setAdding(true);
    }
  };
  const addProducts = (list) => {
    setAdding(false);
    setRows(rs => [...(rs || []), ...list.map((p, i) => ({ id: null, product_id: p.id, product_name: p.name,
      actual_quantity: '0', defect_quantity: '0', focus: i === 0 }))]);
    setErrors(er => ({ ...er, split: null }));
  };
  // Что мешает «Строка принята» — над кнопкой, сразу видно
  const blockHint = rows && rows.some(r => !r.product_id) ? 'Выберите товар в каждой строке'
    : needsDefectInfo && !reason ? 'Выберите причину брака'
      : needsDefectInfo && !line.photos.length ? 'Добавьте фото брака' : null;
  const updRow = (i, r) => { setRows(rs => rs.map((x, j) => (j === i ? r : x))); setErrors(er => ({ ...er, split: null })); };

  return (
    <div ref={rootRef} tabIndex={embedded ? -1 : undefined} role={embedded ? 'group' : undefined} aria-label={embedded ? `Строка: ${lineTitle(line)}` : undefined}
      style={{ display: 'flex', flexDirection: 'column', gap: 16, outline: 'none', paddingBottom: fixedBar ? 'calc(112px + env(safe-area-inset-bottom))' : 0 }}>
      {onBack && (
        <button type="button" className="btn-link" onClick={onBack} style={{ alignSelf: 'flex-start', minHeight: 44 }}>← К списку строк</button>
      )}
      {conflict && <ConflictNote fresh={conflict} onTake={takeTheirs} onKeep={keepMine} />}
      {!embedded && (
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {line.lot_photo_url && (
          <img src={photoSrc(line.lot_photo_url)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', '--ch': '6px', flexShrink: 0 }} />
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: '#1C3D2E' }}>{lineTitle(line)}</div>
          <div style={{ fontSize: 13, color: '#5E6561', marginTop: 2 }}>
            {line.lot_name}{line.length_cm ? `, ${line.length_cm} см` : ''}{line.farm ? ` · ${line.farm}` : ''}
          </div>
          <div style={{ fontSize: 14, marginTop: 4 }}>Заказано: <b>{line.stems} шт</b></div>
          {line.is_mix && line.mix_composition && <div style={{ fontSize: 12, color: '#5E6561' }}>состав по бирже: {line.mix_composition}</div>}
        </div>
      </div>
      )}
      {/* Микс без товаров: подсказка и ЕДИНСТВЕННАЯ ссылка «Несколько товаров» — отдельной строкой на всю
          ширину (в шапке рядом с фото её перекрывало и нажатие не доходило) */}
      {line.mix_hint && !rows && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#B0603C' }}>{line.mix_hint}</span>
          {editable && (
            <button type="button" className="btn-link" disabled={busy} onClick={startSplit}
              style={{ fontSize: 13, minHeight: 44, padding: '0 4px' }}>Несколько товаров</button>
          )}
        </div>
      )}
      {line.accepted && !embedded && <div style={{ fontSize: 13, color: '#1F763B' }}>{lineStateText(line)}</div>}

      {rows ? (
        // Несколько товаров — итог текстом, а не «выключенными» полями
        <div aria-live="polite" style={{ fontSize: 15, color: '#1C3D2E', fontWeight: 600 }}>
          Всего: пришло {actualTotal} шт · брак {defectTotal}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#5E6561' }}>
            Пришло, шт
            <Stepper value={actual} onChange={setActual} disabled={!editable || busy} label="Пришло, шт" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#5E6561' }}>
            Брак, шт
            <Stepper value={defect} onChange={setDefect} disabled={!editable || busy} label="Брак, шт" />
          </div>
        </div>
      )}
      {editable && !rows && !line.mix_hint && (
        <button type="button" className="btn-link" onClick={startSplit} disabled={busy} style={{ alignSelf: 'flex-start', fontSize: 13, minHeight: 36 }}>
          Несколько товаров
        </button>
      )}

      {/* Несколько товаров — на той же подложке, что весь экран: без вложенной карточки, строки через линии */}
      {rows && (
        <div ref={refs.split} style={{ borderTop: `1px solid ${errors.split ? '#CB4D45' : '#DCDDD3'}`, paddingTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...sectionLabel, marginBottom: 0 }}>По товарам</span>
            {editable && !line.accepted && (
              <button type="button" className="btn-link" style={{ minHeight: 36 }}
                onClick={() => { setRows(null); setUnsplit(!!line.splits?.length); setErrors({}); }}>Один товар</button>
            )}
          </div>
          {rows.length === 0 && <div style={{ fontSize: 13, color: '#909894', padding: '8px 0' }}>Выберите товары, которые пришли в этой строке</div>}
          {rows.map((r, i) => (
            <ProductRow key={r.id || `n${i}`} row={r} lineId={line.id} editable={editable && !busy}
              onChange={(nr) => updRow(i, nr)} onRemove={() => setRows(rs => rs.filter((_, j) => j !== i))} />
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 0', borderTop: '1px solid #DCDDD3' }}>
            {editable ? (
              <button ref={addRef} type="button" className="btn-link" style={{ minHeight: 36 }} onClick={() => setAdding(a => !a)}>+ товар</button>
            ) : <span />}
            <span style={{ fontSize: 13, fontWeight: 600, color: splitSum === line.stems ? '#1F763B' : '#B0603C' }}>
              разложено {splitSum} из {line.stems}
            </span>
          </div>
          {adding && (
            <ProductPickerPopover anchorRef={addRef} loadOptions={loadAll} onClose={closeAdd} title="Добавить товары" multi
              excludeIds={rows.map(r => r.product_id).filter(Boolean)} onSelect={(p) => addProducts([p])} onSelectMany={addProducts} />
          )}
          {errors.split && <div style={errText} role="alert">{errors.split}</div>}
        </div>
      )}

      {needsDefectInfo && (
        <div ref={refs.reason}>
          <div style={sectionLabel}>Причина брака</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DEFECT_REASONS.map(([code, label]) => (
              <button key={code} type="button" disabled={!editable || busy} style={{ minHeight: 40 }}
                className={`btn btn-sm ${reason === code ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => { setReason(code); setErrors(er => ({ ...er, reason: null })); }}>
                {label}
              </button>
            ))}
          </div>
          {errors.reason && <div style={errText} role="alert">{errors.reason}</div>}
          <textarea value={note} onChange={e => setNote(e.target.value)} disabled={!editable || busy} maxLength={1000} rows={2}
            placeholder="Пояснение к браку — уйдёт в рекламацию (необязательно)" aria-label="Пояснение к браку"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: 8, fontSize: 16, border: '1px solid #C1C8C4', '--ch': '5px', '--stroke': '#C1C8C4', fontFamily: 'inherit' }} />
        </div>
      )}

      <div ref={refs.photo}>
        <div style={sectionLabel}>Фото{needsDefectInfo ? ' — при браке хотя бы одно' : ''}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {line.photos.map((ph, i) => (
            <div key={ph.id} style={{ position: 'relative' }}>
              <button type="button" onClick={() => setViewAt(i)} aria-label="Открыть фото"
                style={{ padding: 0, border: 'none', background: 'none', cursor: 'zoom-in', display: 'block' }}>
                <img src={photoSrc(ph.url)} alt="Фото приёмки" style={{ width: embedded ? 96 : 64, height: embedded ? 96 : 64, objectFit: 'cover', '--ch': '6px', display: 'block' }} />
              </button>
              {editable && (
                <button type="button" onClick={() => deletePhoto(ph)} disabled={busy} title="Удалить фото" aria-label="Удалить фото"
                  style={{ position: 'absolute', top: -8, right: -8, width: 24, height: 24, '--ch': '8px', border: 'none', background: '#CB4D45', color: '#fff', fontSize: 13, lineHeight: '24px', padding: 0, cursor: 'pointer' }}>×</button>
              )}
            </div>
          ))}
          {editable && (
            <>
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy} style={{ minHeight: 44 }} onClick={() => fileRef.current?.click()}>
                {busy ? 'Загрузка…' : '+ Фото'}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png" capture="environment" multiple onChange={addPhoto} style={{ display: 'none' }} />
            </>
          )}
        </div>
        {errors.photo && <div style={errText} role="alert">{errors.photo}</div>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={sectionLabel}>Оценки (необязательно)</div>
        {RATINGS.map(([key, label]) => {
          const isVase = key === 'rating_vase';
          const disabled = busy || (isVase ? !vaseOpen || line.cancelled : !editable);
          return (
            <div key={key}>
              <div style={{ fontSize: 13, color: '#1C3D2E', marginBottom: 4 }}>
                {label}
                {isVase && !vaseOpen && invoice.vase_from && <span style={{ color: '#909894' }}> — с {ddmm(invoice.vase_from)}</span>}
              </div>
              <Stars value={rates[key]} disabled={disabled} onChange={(v) => setRates(r => ({ ...r, [key]: v }))} />
            </div>
          );
        })}
      </div>

      {(editable || (finalAccepted && vaseOpen && !line.cancelled)) && (
        <div style={fixedBar
          ? { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50, background: '#fff', borderTop: '1px solid #DCDDD3', padding: '8px 16px calc(10px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 6, boxSizing: 'border-box', width: '100%' }
          : { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', position: embedded ? 'static' : 'sticky', bottom: 0, background: embedded ? 'transparent' : '#fff', padding: '10px 0' }}>
          {editable && blockHint && <div style={{ fontSize: 13, color: '#B0603C', fontWeight: 500 }}>{blockHint}</div>}
          {editable && (
            <button type="button" className="btn btn-primary btn-md" onClick={save} disabled={busy} style={{ flex: '1 1 auto', minHeight: 48 }}>
              {busy ? 'Сохранение…' : embedded ? 'Сохранить строку' : 'Строка принята · к списку'}
            </button>
          )}
          {finalAccepted && vaseOpen && !line.cancelled && (
            <button type="button" className="btn btn-primary btn-md" onClick={saveVase} disabled={busy} style={{ flex: '1 1 auto', minHeight: 48 }}>
              Сохранить оценку
            </button>
          )}
        </div>
      )}

      {viewAt != null && line.photos.length > 0 && (
        <PhotoLightbox images={line.photos.map(ph => ({ src: photoSrc(ph.url) }))} startIndex={Math.min(viewAt, line.photos.length - 1)}
          onClose={() => setViewAt(null)} />
      )}
    </div>
  );
}

// ── «Брак после приёмки» ────────────────────────────────────────────────

export function HiddenDefectModal({ line, onClose, onDone, showToast }) {
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [splitId, setSplitId] = useState(line.splits?.length ? line.splits[0].id : null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);   // заказы, которым не хватит после списания — до подтверждения
  const fileRef = useRef(null);
  const previews = useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(u => URL.revokeObjectURL(u)), [previews]);

  const submit = async (confirmed = false) => {
    if (int(qty) <= 0) { showToast('Сколько стеблей?', 'error'); return; }
    if (!reason) { showToast('Укажите причину', 'error'); return; }
    if (!files.length) { showToast('Нужно хотя бы одно фото брака', 'error'); return; }
    setBusy(true);
    try {
      if (!confirmed) {
        const pv = await api.previewProcurementHiddenDefect(line.id, int(qty), splitId);
        if (pv.has_deficit) { setPreview(pv.affected_orders || []); return; }
      }
      const res = await api.addProcurementHiddenDefect(line.id, { quantity: int(qty), reasonCode: reason, note, splitId }, files);
      showToast(res.status === 'sent'
        ? 'Списано. Данные по браку отправлены на рекламацию'
        : 'Списано. Данные по браку сохранены и уйдут на рекламацию сами');
      onDone();
    } catch (e) {
      showToast(e.message || 'Не сохранилось', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Брак после приёмки — ${lineTitle(line)}`} onClose={onClose} confirmOverlayClose
      footer={<>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
        <button type="button" className="btn btn-primary" onClick={() => submit(!!preview)} disabled={busy}>
          {busy ? 'Сохранение…' : preview ? 'Всё равно списать' : 'Списать и в рекламацию'}
        </button>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#5E6561' }}>
          {(() => {
            const sp = (line.splits || []).find(x => x.id === splitId);
            const now = sp ? sp.physical_quantity : line.physical_quantity;
            return now != null ? <b style={{ color: '#1C3D2E' }}>На складе сейчас {now} шт. </b> : null;
          })()}
          Принято {Math.max(0, (line.actual_quantity || 0) - (line.defect_quantity || 0))} шт
          {line.hidden_defect_stems ? `, уже в браке после приёмки ${line.hidden_defect_stems}` : ''}. Стебли спишутся со склада (повреждение).
        </div>
        {line.splits?.length > 0 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            Товар
            <select className="form-input" value={splitId || ''} onChange={e => { setSplitId(parseInt(e.target.value, 10)); setPreview(null); }}>
              {line.splits.map(sp => <option key={sp.id} value={sp.id}>{sp.product_name || 'без товара'}</option>)}
            </select>
          </label>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Сколько стеблей
          <input type="text" inputMode="numeric" value={qty} onChange={e => { setQty(e.target.value.replace(/\D/g, '')); setPreview(null); }} style={numInput} />
        </label>
        {preview && (
          <div style={{ padding: 10, background: '#F9EBCF', color: '#7E5D00', fontSize: 13, '--ch': '6px' }}>
            После списания не хватит заказам:
            {preview.map(o => (
              <div key={`${o.order_id}-${o.product_name}`}>
                №{o.order_number || o.order_id} — {o.product_name}: {o.deficit_quantity} шт{o.delivery_date ? `, доставка ${ddmm(o.delivery_date)}` : ''}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DEFECT_REASONS.map(([code, label]) => (
            <button key={code} type="button" className={`btn btn-sm ${reason === code ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setReason(code)}>{label}</button>
          ))}
        </div>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Пояснение (необязательно)"
          style={{ width: '100%', boxSizing: 'border-box', padding: 8, fontSize: 14, border: '1px solid #C1C8C4', fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {previews.map((u, i) => <img key={u} src={u} alt={`Фото ${i + 1}`} style={{ width: 56, height: 56, objectFit: 'cover' }} />)}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>+ Фото</button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png" capture="environment" multiple style={{ display: 'none' }}
            onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; setFiles(prev => [...prev, ...fs].slice(0, 8)); }} />
        </div>
      </div>
    </Modal>
  );
}

// ── Рекламации накладной ────────────────────────────────────────────────

// Цвет плашки — по смыслу, текст — с сервера (status_text: «ждёт вашего согласования · Р-0002», «подана на биржу · № …»)
const claimBadge = (c) => {
  const text = c.status_text || c.status_label;
  if (c.decision === 'cancelled' || c.market_status === 'withdrawn') return ['#F4F4EF', '#5E6561', text];
  if (c.status === 'error' || c.market_status === 'denied') return ['#FBF1EC', '#B0603C', text];
  if (c.awaiting) return ['#F9EBCF', '#7E5D00', text];
  if (['approved', 'partial', 'compensated', 'closed'].includes(c.market_status)) return ['#DFF2E8', '#1C3D2E', text];
  return ['#F0F8FF', '#26689A', text];
};
// «подать до 04.11, 19:57» — красным, когда осталось меньше 3 часов
function Deadline({ at, bare = false }) {
  if (!at) return null;
  const d = new Date(at);
  const left = d.getTime() - Date.now();
  const label = d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(' ', ' ');
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: left < 3 * 3600 * 1000 ? '#CB4D45' : '#5E6561' }}>
      {bare ? label : left < 0 ? `срок подачи прошёл ${label}` : `подать до ${label}`}
    </span>
  );
}
// Время в журнале: сегодня — «19:57», иначе «03.11 19:57»
const journalTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export function RowMenu({ onHidden }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button type="button" aria-label="Действия со строкой" aria-expanded={open} onClick={() => setOpen(o => !o)}
        style={{ width: 32, height: 32, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: '#5E6561' }}>⋯</button>
      {open && (
        <div role="menu" style={{ position: 'absolute', right: 0, top: 32, zIndex: 20, background: '#fff', border: '1px solid #DCDDD3',
          '--ch': '6px', '--stroke': '#DCDDD3', boxShadow: '0 8px 24px rgba(28,61,46,.12)', minWidth: 190 }}>
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onHidden(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: '#1C3D2E' }}>
            Брак после приёмки…
          </button>
        </div>
      )}
    </span>
  );
}

export function ClaimsSection({ invoiceId, role, showToast, reloadKey, focus = false }) {
  const [claims, setClaims] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);   // { photos, at }
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.getProcurementClaims(invoiceId).then(r => setClaims(r.claims)).catch(() => setClaims([]));
  }, [invoiceId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  // Открыли из значка «Рекламации: ждут согласования» — сразу к разделу
  const boxRef = useRef(null);
  useEffect(() => {
    if (focus && claims && claims.length) boxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus, claims]);

  const act = async (c, action) => {
    if (action === 'cancel' && !(await nfConfirm('Не подавать рекламацию?', { confirmText: 'Не подавать' }))) return;
    setBusy(true);
    try {
      const res = await api.decideProcurementClaim(c.id, action);
      setClaims(cs => cs.map(x => (x.id === c.id ? res : x)));
      showToast(action === 'approve' ? 'Согласовано — рекламация готова к подаче' : action === 'cancel' ? 'Рекламация не будет подана' : 'Отправим ещё раз');
    } catch (e) {
      showToast(e.message || 'Не получилось', 'error');
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (c) => {
    if (!replyText.trim() && !replyFiles.length) return;
    setBusy(true);
    try {
      const res = await api.replyProcurementClaim(c.id, replyText.trim(), replyFiles);
      setClaims(cs => cs.map(x => (x.id === c.id ? res : x)));
      setReplyFor(null); setReplyText(''); setReplyFiles([]);
      showToast('Ответ отправлен');
    } catch (e) {
      showToast(e.message || 'Не отправилось', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!claims || claims.length === 0) return null;
  // «2 ждут вас · ближайший срок 04.11, 19:57» (красным, когда меньше 3 часов)
  const waiting = claims.filter(c => c.awaiting);
  const nearest = waiting.map(c => c.deadline_at).filter(Boolean).sort()[0];
  return (
    <div ref={boxRef} id="claims-section" style={{ marginTop: 16, borderTop: '1px solid #EBEDEC', paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <span style={{ ...sectionLabel, fontSize: 12, marginBottom: 0 }}>Рекламации ({claims.length})</span>
        {waiting.length > 0 && (
          <span style={{ fontSize: 13, color: '#7E5D00', fontWeight: 600 }}>
            {role === 'owner' ? `${waiting.length} ждут вас` : `${waiting.length} ждут владельца`}
            {nearest && <> · ближайший срок <Deadline at={nearest} bare /></>}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {claims.map(c => {
          const [bg, fg, text] = claimBadge(c);
          return (
            <div key={c.id} style={{ padding: '12px 0', borderTop: '1px solid #EBEDEC' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 600, color: '#1C3D2E' }}>
                  {c.product_name} · {c.stems} шт · {c.reason_label || 'брак'}
                  <span style={{ fontWeight: 400, color: '#909894', fontSize: 12 }}> · {c.kind_label}</span>
                </div>
                <span style={{ padding: '2px 8px', background: bg, color: fg, fontSize: 12, fontWeight: 600, '--ch': '5px', whiteSpace: 'nowrap' }}>
                  {text}
                </span>
              </div>
              {c.deadline_at && <div style={{ marginTop: 2 }}><Deadline at={c.deadline_at} /></div>}
              <div style={{ fontSize: 13, color: '#5E6561', marginTop: 4 }}>{c.comment}</div>
              {c.money_text && <div style={{ fontSize: 12, color: '#1C3D2E', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{c.money_text}</div>}
              {c.changed_note && <div role="status" style={{ fontSize: 12, color: '#B0603C', marginTop: 4, fontWeight: 600 }}>{c.changed_note}</div>}
              {c.last_error && <div style={{ fontSize: 12, color: '#B0603C', marginTop: 4 }}>{c.last_error}</div>}
              {c.photos.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  {c.photos.map((ph, i) => (
                    <button key={ph.id} type="button" onClick={() => setView({ photos: c.photos, at: i })} aria-label="Открыть фото"
                      style={{ padding: 0, border: 'none', background: 'none', cursor: 'zoom-in' }}>
                      <img src={photoSrc(ph.url)} alt="" style={{ width: 44, height: 44, objectFit: 'cover', display: 'block' }} />
                    </button>
                  ))}
                </div>
              )}
              {c.journal.length > 0 && (
                <details style={{ marginTop: 8, fontSize: 12, color: '#5E6561' }}>
                  <summary style={{ cursor: 'pointer' }}>Журнал ({c.journal.length})</summary>
                  {c.journal.map((e, i) => (
                    <div key={i} style={{ marginTop: 4 }}>
                      {e.at && <span style={{ color: '#909894' }}>{journalTime(e.at)} · </span>}
                      {e.who ? `${e.who}: ` : ''}{e.text}
                    </div>
                  ))}
                </details>
              )}
              {c.approved_by_name && (
                <div style={{ fontSize: 12, color: '#909894', marginTop: 4 }}>
                  {c.decision === 'approved' ? 'Согласовал' : 'Решил не подавать'}: {c.approved_by_name}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {c.can_decide && <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => act(c, 'approve')}>Согласовать и подать</button>}
                {c.can_decide && <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => act(c, 'cancel')}>Не подавать</button>}
                {role !== 'owner' && c.awaiting && <span style={{ fontSize: 12, color: '#909894' }}>согласует владелец</span>}
                {c.can_retry && <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => act(c, 'retry')}>Повторить</button>}
                {c.can_reply && replyFor !== c.id && <button type="button" className="btn-link" onClick={() => setReplyFor(c.id)}>Ответить</button>}
              </div>
              {replyFor === c.id && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={2} maxLength={1000} placeholder="Ответ на вопрос биржи"
                    style={{ width: '100%', boxSizing: 'border-box', padding: 8, fontSize: 14, border: '1px solid #C1C8C4', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
                      + Фото{replyFiles.length ? ` (${replyFiles.length})` : ''}
                    </button>
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png" multiple style={{ display: 'none' }}
                      onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; setReplyFiles(p => [...p, ...fs].slice(0, 8)); }} />
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => sendReply(c)}>Отправить</button>
                    <button type="button" className="btn-link" onClick={() => { setReplyFor(null); setReplyFiles([]); }}>Отмена</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {view && (
        <PhotoLightbox images={view.photos.map(ph => ({ src: photoSrc(ph.url) }))} startIndex={view.at} onClose={() => setView(null)} />
      )}
    </div>
  );
}
